/**
 * BlayzBot — unified assistant mascot.
 *
 * Two modes accessible from a single floating mascot on the right-middle
 * of every app page:
 *   1. Tips mode (default) — rules-based, context-aware navigation
 *      nudges. No LLM calls.
 *   2. Chat mode — AI-powered support agent using the existing
 *      /api/support/chat endpoint (Claude Sonnet via backend) with a
 *      "Talk to a human" escalation flow. Session persistence and
 *      poll-for-admin-reply mirror the previous SupportChatWidget so
 *      admin threads still work end-to-end.
 *
 * The mascot is dismissible per browser session and reopenable via a
 * "Ask Blayz" chip so it never blocks content.
 *
 * Props:
 *   context: 'dashboard' | 'analysis' (drives Tips content)
 *   state: {
 *     uploading?, hasPendingPdf?, consentGiven?, historyCount?,
 *     auditReady?, careerStage?, careerObjective?, hasPostTabs?,
 *   }
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, MessageCircle, ChevronRight, Send, Loader2, Sparkles, HandshakeIcon, ArrowLeft } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth-context';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

const MASCOT_URL = 'https://customer-assets-wrfwihn1.emergentagent.net/job_f1e2a0c0-4691-45ed-aad2-ff99b8029b76/artifacts/0m9snvgb_3d-cartoon-businesswoman-happy-working-girl-suit.webp';
const DISMISS_KEY = 'blayzrr_bot_dismissed_v1';
const REOPEN_KEY = 'blayzrr_bot_reopened_v1';
const SUPPORT_SESSION_KEY = 'blayzrr.support.session';
const SUPPORT_CONTACT_KEY = 'blayzrr.support.contact';

function pickTips(context, state) {
  const tips = [];
  if (context === 'dashboard') {
    if (state.uploading) {
      tips.push({ title: "I'm reading your PDF…", body: "This usually takes 20-40 seconds. I'll show you the wizard when it's ready." });
      return tips;
    }
    if (state.hasPendingPdf) {
      tips.push({ title: 'Your free-audit PDF is already here!', body: 'Tap "Run full audit" on the blue card to skip re-uploading. Same file, deeper analysis.' });
    }
    if (!state.consentGiven) {
      tips.push({ title: "One quick tick and we're off", body: 'Confirm the ownership + Terms line below the upload box. It unlocks the analyser.' });
    } else {
      tips.push({ title: 'Drop your LinkedIn PDF in the dashed box', body: 'On LinkedIn: your profile → Resources → Save to PDF. Then drag it here or tap Choose PDF.' });
    }
    if ((state.historyCount || 0) === 0) {
      tips.push({ title: 'Pick your objective first', body: 'Choose Student / Fresher / Corporate and your goal — I tailor the scoring rubric to it.' });
    } else {
      tips.push({ title: 'Re-audit in ~30 days', body: "Rerun after you apply the fixes and I'll show you exactly which levers moved your score." });
    }
    tips.push({ title: 'Prefer to type it in?', body: 'Switch to the "Type manually" tab — a guided 6-step wizard takes ~3-4 minutes.' });
  } else if (context === 'analysis') {
    if (!state.auditReady) {
      tips.push({ title: 'Reading your profile…', body: 'Positioning, keywords, proof and plan — all coming up. Around 40-90 seconds.' });
      return tips;
    }
    tips.push({ title: 'Start on the Overview tab', body: 'Your score, positioning gap and #1 improvement are up top. Everything else expands from there.' });
    tips.push({ title: 'Rewrite your headline first', body: 'The Profile Rewrite tab has 3 headline options — copy the recommended one straight into LinkedIn.' });
    if (state.hasPostTabs) {
      tips.push({ title: 'Ready to post?', body: "Try PoV Posts or Prompt Posts under the Post Creation tabs — I'll draft one in your voice." });
    }
    tips.push({ title: 'Run the 30-Day Plan', body: 'The last tab breaks everything into 4 weekly sprints. Bookmark it and knock one item off a day.' });
  } else {
    // Global / marketing / billing pages — no page-specific nudge, just
    // surface Blayz as the door into AI + human support.
    tips.push({ title: "Hi, I'm Blayz — how can I help?", body: 'Ask me about pricing, running an audit, the free score or anything else. I can bring in a real human if you need one.' });
  }
  return tips;
}

// ---------------- Support chat helpers (kept in-file so BlayzBot is
// the single mount point for both tips and chat) ----------------
function genSessionId() {
  return 'sess-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

function readContact() {
  try {
    const raw = window.localStorage.getItem(SUPPORT_CONTACT_KEY);
    if (!raw) return { email: '', name: '' };
    const p = JSON.parse(raw);
    return { email: p.email || '', name: p.name || '' };
  } catch { return { email: '', name: '' }; }
}

function saveContact(c) {
  try { window.localStorage.setItem(SUPPORT_CONTACT_KEY, JSON.stringify(c)); } catch { /* ignore */ }
}

function useSupportSessionId() {
  const [sid] = useState(() => {
    if (typeof window === 'undefined') return genSessionId();
    let s = window.localStorage.getItem(SUPPORT_SESSION_KEY);
    if (!s) {
      s = genSessionId();
      try { window.localStorage.setItem(SUPPORT_SESSION_KEY, s); } catch { /* ignore */ }
    }
    return s;
  });
  return sid;
}

function MessageBubble({ role, content }) {
  const isUser = role === 'user';
  const isAdmin = role === 'admin';
  return (
    <div className={cn('flex w-full', isUser ? 'justify-end' : 'justify-start')} data-testid={`blayz-chat-message-${role}`}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-[13px] leading-relaxed whitespace-pre-wrap',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : isAdmin
              ? 'bg-[hsl(var(--success))]/10 border border-[hsl(var(--success))]/30 text-foreground rounded-bl-sm'
              : 'bg-secondary text-foreground rounded-bl-sm',
        )}
      >
        {isAdmin && (
          <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-[hsl(var(--success))] mb-1">Blayzrr team</div>
        )}
        {content}
      </div>
    </div>
  );
}

// ---------------- Chat panel ----------------
function ChatPanel({ onBack }) {
  const { user } = useAuth() || {};
  const sid = useSupportSessionId();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [escalated, setEscalated] = useState(false);
  const [needContact, setNeedContact] = useState(false);
  const [contact, setContact] = useState(readContact);
  const scrollRef = useRef(null);

  const pollForReplies = useCallback(async () => {
    try {
      const res = await api.get(`/support/thread/${sid}`);
      const t = res.data?.thread;
      const msgs = res.data?.messages || [];
      if (t?.status === 'escalated') setEscalated(true);
      setMessages(msgs);
    } catch { /* silent */ }
  }, [sid]);

  useEffect(() => {
    pollForReplies();
    const int = setInterval(pollForReplies, 12000);
    return () => clearInterval(int);
  }, [pollForReplies]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  useEffect(() => {
    if (user) { setNeedContact(false); return; }
    const hasSentBefore = messages.some((m) => m.role === 'user');
    if (hasSentBefore) { setNeedContact(false); return; }
    if (!contact.email) setNeedContact(true);
  }, [user, messages, contact.email]);

  const send = async (text) => {
    const body = (text ?? input).trim();
    if (!body || sending) return;
    setSending(true);
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: 'user', content: body, created_at: new Date().toISOString() }]);
    setInput('');
    try {
      const payload = { session_id: sid, message: body };
      if (!user) { payload.email = contact.email || ''; payload.name = contact.name || ''; }
      const res = await api.post('/support/chat', payload);
      const reply = res.data?.reply || '';
      setMessages((prev) => [...prev, { id: `local-r-${Date.now()}`, role: 'assistant', content: reply, created_at: new Date().toISOString() }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Sorry — could not reach Blayz just now. Please retry.';
      setMessages((prev) => [...prev, { id: `local-err-${Date.now()}`, role: 'assistant', content: detail, created_at: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const escalate = async () => {
    if (sending || escalated) return;
    setSending(true);
    try {
      const res = await api.post('/support/escalate', { session_id: sid });
      setEscalated(true);
      setMessages((prev) => [...prev, { id: `local-esc-${Date.now()}`, role: 'assistant', content: res.data?.reply || '', created_at: new Date().toISOString() }]);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Please send at least one message first.';
      setMessages((prev) => [...prev, { id: `local-esc-err-${Date.now()}`, role: 'assistant', content: detail, created_at: new Date().toISOString() }]);
    } finally { setSending(false); }
  };

  const submitContact = () => {
    const email = contact.email.trim();
    const name = contact.name.trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;
    saveContact({ email, name });
    setContact({ email, name });
    setNeedContact(false);
  };

  const greeting = useMemo(() => (
    messages.length === 0 && !needContact
      ? `Hi${user?.name ? ` ${user.name.split(' ')[0]}` : ''}! I'm Blayz. Ask me about pricing, features, running an audit or anything else — and I'll bring in a real human if I can't answer.`
      : null
  ), [messages.length, needContact, user]);

  return (
    <div className="w-[min(92vw,360px)] h-[min(560px,calc(100vh-6rem))] bg-card border border-primary/25 rounded-2xl shadow-[0_24px_60px_-24px_rgba(0,0,0,0.4)] flex flex-col overflow-hidden" data-testid="blayz-chat-panel">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 bg-primary text-primary-foreground shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="p-1 rounded-md hover:bg-white/10 transition-colors shrink-0"
          data-testid="blayz-chat-back"
          aria-label="Back to tips"
        >
          <ArrowLeft size={16} />
        </button>
        <div className="flex items-center gap-2 min-w-0">
          <img src={MASCOT_URL} alt="" className="h-7 w-7 rounded-full object-cover object-top border border-white/40" draggable="false" />
          <div className="min-w-0">
            <div className="font-semibold text-sm leading-tight truncate">Chat with Blayz</div>
            <div className="text-[10px] opacity-85 leading-tight">{escalated ? 'Team notified · reply in 4-5 working hours' : 'AI-powered · replies in seconds'}</div>
          </div>
        </div>
        <span className="w-6" />
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2.5 bg-background/40">
        {greeting && <MessageBubble role="assistant" content={greeting} />}
        {messages.map((m) => <MessageBubble key={m.id || `${m.role}-${m.created_at}`} role={m.role} content={m.content} />)}
        {sending && (
          <div className="flex items-center gap-2 text-[11px] text-muted-foreground pl-1">
            <Loader2 size={12} className="animate-spin" /> Blayz is thinking…
          </div>
        )}
      </div>

      {/* Contact form or input */}
      {needContact ? (
        <div className="border-t border-border p-3 space-y-2 bg-card shrink-0" data-testid="blayz-chat-contact-form">
          <p className="text-[11px] text-muted-foreground">Share your email so we can follow up if needed.</p>
          <Input type="email" value={contact.email} onChange={(e) => setContact((c) => ({ ...c, email: e.target.value }))} placeholder="you@company.com" className="h-9 text-sm" data-testid="blayz-chat-contact-email" />
          <Input value={contact.name} onChange={(e) => setContact((c) => ({ ...c, name: e.target.value }))} placeholder="Your name (optional)" className="h-9 text-sm" data-testid="blayz-chat-contact-name" />
          <Button onClick={submitContact} className="w-full h-9 rounded-lg" data-testid="blayz-chat-contact-submit">Start chatting</Button>
        </div>
      ) : (
        <div className="border-t border-border p-3 space-y-2 bg-card shrink-0">
          <div className="flex gap-2 items-end">
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder={escalated ? 'Add more context for the team…' : 'Ask Blayz anything…'}
              className="h-10 text-sm"
              disabled={sending}
              data-testid="blayz-chat-input"
            />
            <Button onClick={() => send()} disabled={sending || !input.trim()} size="icon" className="h-10 w-10 rounded-lg shrink-0" data-testid="blayz-chat-send" aria-label="Send">
              {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </Button>
          </div>
          {!escalated && (
            <button type="button" onClick={escalate} disabled={sending || messages.length === 0} className="text-[11px] text-muted-foreground hover:text-primary transition-colors disabled:opacity-50 flex items-center gap-1.5" data-testid="blayz-chat-escalate">
              <HandshakeIcon size={11} /> Talk to a human
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------- Root component ----------------
export default function BlayzBot({ context = 'dashboard', state = {} }) {
  const tips = useMemo(() => pickTips(context, state), [context, state]);
  // Default = collapsed. Users can reopen it via the floating mascot
  // (see the `reopen` control below). A session-scoped dismissal is
  // still respected — reopening in the same tab keeps it open.
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('tips'); // 'tips' | 'chat'
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    try {
      // Honour an explicit "reopened" flag if the user reopened it in
      // this session. Otherwise stay collapsed by default so it doesn't
      // block the hero form.
      if (sessionStorage.getItem(REOPEN_KEY) === '1') setOpen(true);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { setIdx(0); }, [tips.length, context]);

  const dismiss = () => {
    setOpen(false);
    setMode('tips');
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
      sessionStorage.removeItem(REOPEN_KEY);
    } catch { /* ignore */ }
  };
  const reopen = () => {
    setOpen(true);
    try {
      sessionStorage.removeItem(DISMISS_KEY);
      sessionStorage.setItem(REOPEN_KEY, '1');
    } catch { /* ignore */ }
  };

  const openChat = () => setMode('chat');
  const backToTips = () => setMode('tips');

  const tip = tips.length ? tips[Math.min(idx, tips.length - 1)] : null;

  return (
    <div className="fixed top-1/2 -translate-y-1/2 right-2 sm:right-4 z-40 flex flex-col items-end pointer-events-none" data-testid="blayz-bot">
      {open ? (
        <div className="pointer-events-auto flex flex-col-reverse sm:flex-row items-end sm:items-center gap-2 sm:gap-3">
          {mode === 'chat' ? (
            <ChatPanel onBack={backToTips} />
          ) : (
            tip && (
              <div
                className="relative order-2 sm:order-1 flex-1 max-w-[min(92vw,360px)] rounded-2xl border border-primary/25 bg-card/95 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.3)] backdrop-blur px-3.5 py-3 text-[13px] leading-snug animate-in fade-in slide-in-from-bottom-2 duration-300"
                data-testid="blayz-bot-bubble"
              >
                <button
                  type="button"
                  onClick={dismiss}
                  className="absolute -top-2 -right-2 h-6 w-6 inline-flex items-center justify-center rounded-full bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors shadow-sm"
                  aria-label="Dismiss Blayz"
                  data-testid="blayz-bot-dismiss"
                >
                  <X size={12} />
                </button>
                <p className="font-semibold text-foreground" data-testid="blayz-bot-title">{tip.title}</p>
                <p className="mt-1 text-muted-foreground" data-testid="blayz-bot-body">{tip.body}</p>
                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[10px] font-mono-edge tracking-[0.14em] uppercase text-muted-foreground">Blayz · tip {Math.min(idx + 1, tips.length)} / {tips.length}</span>
                  {tips.length > 1 && (
                    <button
                      type="button"
                      onClick={() => setIdx((i) => (i + 1) % tips.length)}
                      className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary hover:bg-primary/15 px-2.5 py-1 text-[11px] font-medium transition-colors"
                      data-testid="blayz-bot-next"
                    >
                      Next tip <ChevronRight size={11} />
                    </button>
                  )}
                </div>
                {/* Ask anything CTA — bridges Tips into full chat */}
                <button
                  type="button"
                  onClick={openChat}
                  className="mt-2.5 w-full inline-flex items-center justify-center gap-1.5 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 px-3 py-2 text-[12px] font-medium shadow-sm transition-all"
                  data-testid="blayz-bot-open-chat"
                >
                  <Sparkles size={12} /> Ask Blayz anything
                </button>
                <span className="hidden sm:block absolute -right-1.5 bottom-6 h-3 w-3 rotate-45 border-r border-b border-primary/25 bg-card" />
              </div>
            )
          )}

          {/* Mascot — clickable to open chat */}
          <button
            type="button"
            onClick={mode === 'chat' ? backToTips : openChat}
            className="order-1 sm:order-2 relative shrink-0 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 rounded-full"
            data-testid="blayz-bot-mascot"
            aria-label={mode === 'chat' ? 'Back to tips' : 'Open chat with Blayz'}
          >
            <div className="absolute inset-0 rounded-full bg-primary/25 blur-2xl -z-10" aria-hidden="true" />
            <img
              src={MASCOT_URL}
              alt="Blayz — your LinkedIn coach"
              className="h-16 w-16 sm:h-20 sm:w-20 object-cover object-top rounded-full border-2 border-primary/40 bg-white shadow-lg select-none group-hover:scale-105 transition-transform"
              draggable="false"
            />
            <span className="absolute -bottom-1 -right-1 inline-flex h-4 w-4 rounded-full bg-emerald-400 border-2 border-card" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={reopen}
          className="pointer-events-auto inline-flex items-center gap-2 rounded-full border border-primary/30 bg-card/90 hover:bg-card shadow-md px-3 py-1.5 text-[12px] font-medium text-foreground backdrop-blur transition-all"
          data-testid="blayz-bot-reopen"
        >
          <MessageCircle size={13} className="text-primary" /> Ask Blayz
        </button>
      )}
    </div>
  );
}
